import { t, useLanguage, formatMoney } from "../../i18n";
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicAPI } from '../../services/api';
import { useCartStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { ShoppingCart, Plus, Minus, ArrowLeft, Settings2, ReceiptText, Clock, AlertTriangle, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { trackJourney } from '../../lib/journey';
export default function Menu() {
  useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [optionsModal, setOptionsModal] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const headerRef = useRef(null);
  const categoryNavRef = useRef(null);
  const [activeHappyHours, setActiveHappyHours] = useState([]);
  const [availability, setAvailability] = useState({
    enabled: false,
    is_open: true,
    message: ''
  });
  const {
    items,
    addItem,
    updateQuantity,
    getItemCount,
    establishmentId,
    tableId
  } = useCartStore();
  const itemCount = getItemCount();
  const isClosed = availability.enabled && !availability.is_open;
  useEffect(() => {
    const loadMenu = async () => {
      let estId = establishmentId;
      if (!estId) {
        const cartData = localStorage.getItem('glassorder-cart');
        if (cartData) {
          try {
            estId = JSON.parse(cartData).state?.establishmentId;
          } catch (e) {

            // ignore
          }
        }
      }
      if (!estId) {
        toast.error(t("Veuillez scanner le QR code de votre table"));
        navigate('/');
        return;
      }
      try {
        const [menuResponse, availabilityResponse, happyHoursResponse] = await Promise.all([publicAPI.getMenu(estId), publicAPI.getAvailability(estId), publicAPI.getActiveHappyHours(estId)]);
        setCategories(menuResponse.data.categories || []);
        setProducts(menuResponse.data.products || []);
        trackJourney('menu_viewed', tableId);
        setAvailability(availabilityResponse.data || {
          enabled: false,
          is_open: true,
          message: ''
        });
        setActiveHappyHours(happyHoursResponse.data || []);
        if ((menuResponse.data.categories || []).length > 0) {
          setActiveCategory(menuResponse.data.categories[0].id);
        }
      } catch (err) {
        toast.error(t("Erreur lors du chargement du menu"));
      } finally {
        setLoading(false);
      }
    };
    loadMenu();
  }, [establishmentId, navigate, tableId]);
  const happyHourCategoryIds = useMemo(() => {
    const ids = new Set();
    for (const hh of activeHappyHours) {
      for (const categoryId of hh.category_ids || []) {
        ids.add(categoryId);
      }
    }
    return ids;
  }, [activeHappyHours]);
  const getHappyHourDiscountPercent = categoryId => {
    if (!categoryId) return 0;
    let maxDiscount = 0;
    for (const hh of activeHappyHours) {
      if ((hh.category_ids || []).includes(categoryId)) {
        maxDiscount = Math.max(maxDiscount, hh.discount_percent || 0);
      }
    }
    return maxDiscount;
  };
  const getProductDisplayPricing = product => {
    const discountPercent = getHappyHourDiscountPercent(product.category_id);
    const originalPrice = Number(product.price || 0);
    if (discountPercent <= 0) {
      return {
        originalPrice,
        finalPrice: originalPrice,
        happyHourApplied: false,
        discountPercent: 0
      };
    }
    const discountAmount = originalPrice * discountPercent / 100;
    const finalPrice = Math.round((originalPrice - discountAmount + Number.EPSILON) * 100) / 100;
    return {
      originalPrice,
      finalPrice,
      happyHourApplied: true,
      discountPercent
    };
  };
  const filteredProducts = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('fr');
    const categoryOrder = new Map(categories.map((category, index) => [category.id, index]));
    return products.filter(p => !query || `${p.name} ${p.description}`.toLocaleLowerCase('fr').includes(query))
      .sort((a,b) => (categoryOrder.get(a.category_id) ?? categories.length) - (categoryOrder.get(b.category_id) ?? categories.length));
  }, [products, categories, search]);
  const visibleCategories = useMemo(() => categories.filter(category => products.some(product => product.category_id === category.id)), [categories, products]);
  const happyHourCategoryNames = useMemo(() => {
    return categories.filter(cat => happyHourCategoryIds.has(cat.id)).map(cat => cat.name);
  }, [categories, happyHourCategoryIds]);
  const selectCategory = categoryId => {
    setActiveCategory(categoryId);
    const scroll = () => {
      const heading = document.getElementById(`menu-section-${categoryId}`);
      if (heading) window.scrollTo({ top: window.scrollY + heading.getBoundingClientRect().top - (headerRef.current?.offsetHeight || 0) - 12, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    if (search.trim()) { setSearch(''); requestAnimationFrame(scroll); }
    else scroll();
  };
  useEffect(() => {
    if (loading || search.trim()) return;
    let frame;
    const update = () => {
      frame = null;
      const limit = (headerRef.current?.offsetHeight || 0) + 32;
      let current = visibleCategories[0]?.id;
      for (const category of visibleCategories) {
        if (document.getElementById(`menu-section-${category.id}`)?.getBoundingClientRect().top <= limit) current = category.id;
      }
      if (window.scrollY > 0 && window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 4) current = visibleCategories[visibleCategories.length - 1]?.id;
      if (current) setActiveCategory(current);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (frame) cancelAnimationFrame(frame); };
  }, [loading, search, visibleCategories]);
  useEffect(() => {
    const nav = categoryNavRef.current;
    const button = nav && Array.from(nav.querySelectorAll('button')).find(node => node.dataset.category === activeCategory);
    if (button) nav.scrollTo?.({ left: button.offsetLeft - nav.clientWidth / 2 + button.offsetWidth / 2, behavior: 'auto' });
  }, [activeCategory]);
  const getItemQuantity = productId => {
    return items.filter(i => i.product_id === productId).reduce((sum, item) => sum + item.quantity, 0);
  };
  const handleAddToCart = product => {
    if (isClosed) {
      toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    if (product.options?.length > 0) {
      setOptionsModal(product);
      setSelectedOptions([]);
      return;
    }
    addItem(product, []);
    toast.success(t("{name} ajouté au panier", {
      name: product.name
    }));
    publicAPI.trackEvent({
      event_type: 'product_added_to_cart',
      table_id: tableId,
      metadata: {
        product_id: product.id,
        product_name: product.name
      }
    });
  };
  const handleIncreaseQuantity = (productId, quantity) => {
    if (isClosed) {
      toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    updateQuantity(productId, quantity + 1);
  };
  const handleConfirmWithOptions = () => {
    if (!optionsModal) return;
    if (isClosed) {
      toast.error(t(availability.message || '') || t("L'établissement est actuellement fermé"));
      return;
    }
    addItem(optionsModal, selectedOptions);
    const pricing = getProductDisplayPricing(optionsModal);
    const optionsCost = selectedOptions.reduce((sum, name) => {
      const opt = optionsModal.options.find(o => o.name === name);
      return sum + (opt?.price || 0);
    }, 0);
    const totalPrice = pricing.finalPrice + optionsCost;
    toast.success(t("{name} ajouté ({price})", {
      name: optionsModal.name,
      price: formatMoney(totalPrice)
    }));
    publicAPI.trackEvent({
      event_type: 'product_added_to_cart',
      table_id: tableId,
      metadata: {
        product_id: optionsModal.id,
        product_name: optionsModal.name,
        options: selectedOptions
      }
    });
    setOptionsModal(null);
    setSelectedOptions([]);
  };
  const toggleOption = optName => {
    setSelectedOptions(prev => prev.includes(optName) ? prev.filter(n => n !== optName) : [...prev, optName]);
  };
  const getOptionsTotal = () => {
    if (!optionsModal) return 0;
    return selectedOptions.reduce((sum, name) => {
      const opt = optionsModal.options.find(o => o.name === name);
      return sum + (opt?.price || 0);
    }, 0);
  };
  if (loading) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-pulse text-gray-400">{t("Chargement du menu...")}</div>
      </div>;
  }
  return <div className="min-h-screen bg-[#F9FAFB] pb-[calc(7rem+env(safe-area-inset-bottom))]">
      <header ref={headerRef} className="sticky top-0 z-20 border-b border-gray-100 bg-white/95 backdrop-blur-lg">
        <div className="flex items-center gap-3 px-4 pt-3">
          <button type="button" aria-label={t("Retour au menu")} onClick={() => navigate(`/entry/${establishmentId}`)} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-gray-100" data-testid="menu-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 text-lg font-bold">{t("Menu")}</h1>
          <button type="button" aria-label={t("Mes commandes")} onClick={() => navigate('/my-orders')} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-white" data-testid="menu-my-orders-btn">
            <ReceiptText className="h-5 w-5" />
          </button>
        </div>
        <div className="px-4 py-2">
          <label htmlFor="menu-search" className="sr-only">{t("Rechercher dans le menu")}</label>
          <input id="menu-search" type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder={t("Un plat, une boisson…")} className="min-h-11 w-full rounded-xl border bg-white px-4 py-2" />
        </div>
        {!search.trim() && visibleCategories.length > 1 && <nav ref={categoryNavRef} aria-label={t("Catégories")} className="relative flex gap-5 overflow-x-auto px-4 hide-scrollbar">
          {visibleCategories.map(category => <button type="button" key={category.id} data-category={category.id} aria-current={activeCategory === category.id ? 'location' : undefined} onClick={() => selectCategory(category.id)} className={`shrink-0 border-b-2 py-3 text-sm font-semibold ${activeCategory === category.id ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-600'}`} data-testid={`category-${category.id}`}>{category.name}</button>)}
        </nav>}
      </header>

      {activeHappyHours.length > 0 && <div className="px-4 pt-4">
          <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-4 text-white shadow-sm">

            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white/20 p-2 mt-0.5">
                <Sparkles className="w-4 h-4" />
              </div>

              <div className="flex-1">
                <p className="font-bold text-sm">{t("Happy Hour en cours")}</p>

                <p className="text-xs opacity-90 mt-1 leading-5">
                  {activeHappyHours.map(hh => {
                const categoryNames = categories.filter(cat => (hh.category_ids || []).includes(cat.id)).map(cat => cat.name);
                if (!categoryNames.length) {
                  return `${hh.name}: -${hh.discount_percent}%`;
                }
                return t("{name}: -{percent}% sur {categories}", {
                  name: hh.name,
                  percent: hh.discount_percent,
                  categories: categoryNames.join(", ")
                });
              }).join(' • ')}
                </p>

                {happyHourCategoryNames.length > 0 && <div className="flex flex-wrap gap-2 mt-3">
                    {happyHourCategoryNames.map(name => <button key={name} type="button" onClick={() => {
                const category = categories.find(c => c.name === name);
                if (category) selectCategory(category.id);
              }} className="text-xs px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors">

                        {name}
                      </button>)}
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>}

      {isClosed && <div className="px-4 pt-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-amber-100 p-2">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <p className="font-semibold">{t("Établissement actuellement fermé")}</p>
                <p className="mt-1 text-sm leading-6">
                  {t(availability.message || '') || t("Les commandes ne sont pas disponibles pour le moment.")}
                </p>
                <div className="mt-2 flex items-center gap-2 text-xs text-amber-800">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{t("Le menu reste consultable, mais l’ajout au panier est bloqué.")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>}

      <div className="p-4 space-y-3">
          {filteredProducts.length === 0 ? <div className="rounded-2xl border border-gray-200 bg-white p-6 text-center text-gray-500">{t("Aucun produit ne correspond à votre recherche ou à vos filtres.")}

          </div> : filteredProducts.map((product, index) => {
          const quantity = getItemQuantity(product.id);
          const hasOptions = product.options?.length > 0;
          const pricing = getProductDisplayPricing(product);
            return <div key={product.id} className="space-y-3">
            {!search.trim() && (index === 0 || filteredProducts[index - 1].category_id !== product.category_id) && <h2 id={`menu-section-${product.category_id}`} className="pt-5 pb-2 text-xl font-bold text-slate-900">{categories.find(category => category.id === product.category_id)?.name || t("Menu")}</h2>}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            delay: Math.min(index * 0.02, 0.12)
          }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-4" data-testid={`product-card-${product.id}`}>

                  <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                    {product.image ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <ShoppingCart className="w-8 h-8" />
                      </div>}
                  </div>

                  <div className="flex-1 flex flex-col justify-between min-w-0">
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="font-semibold text-gray-900 break-words" style={{
                    fontFamily: 'Manrope'
                  }}>

                          {product.name}
                        </h3>

                        {hasOptions && <Settings2 className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />}

                        {pricing.happyHourApplied && <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0">
                            -{pricing.discountPercent}%
                          </Badge>}
                      </div>

                      <p className="text-sm text-gray-500">{product.description}</p>
                      {!!product.allergens?.length && <p className="mt-1 text-xs text-slate-600">{t("Allergènes :")} {product.allergens.join(', ')}</p>}

                      {hasOptions && <p className="text-xs text-purple-500 mt-0.5">
                          {t(product.options.length === 1 ? "{count} option disponible" : "{count} options disponibles", {
                    count: product.options.length
                  })}
                        </p>}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                      <div className="flex flex-col">
                        <span className="text-lg font-bold text-[#111827]" style={{
                    fontFamily: 'JetBrains Mono'
                  }}>

                          {pricing.finalPrice === 0 ? t("Gratuit") : formatMoney(pricing.finalPrice)}
                        </span>

                        {pricing.happyHourApplied && <span className="text-xs text-gray-400 line-through" style={{
                    fontFamily: 'JetBrains Mono'
                  }}>

                            {formatMoney(pricing.originalPrice)}
                          </span>}
                      </div>

                      {quantity > 0 && !hasOptions ? <div className="flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1">
                          <button onClick={() => updateQuantity(product.id, quantity - 1)} className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center active:scale-95 transition-transform" data-testid={`decrease-${product.id}`}>

                            <Minus className="w-4 h-4" />
                          </button>

                          <span className="font-semibold min-w-[20px] text-center" style={{
                    fontFamily: 'JetBrains Mono'
                  }}>

                            {quantity}
                          </span>

                          <button onClick={() => handleIncreaseQuantity(product.id, quantity)} disabled={isClosed} className={`w-8 h-8 rounded-full shadow flex items-center justify-center active:scale-95 transition-transform ${isClosed ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#7C3AED] text-white'}`} data-testid={`increase-${product.id}`}>

                            <Plus className="w-4 h-4" />
                          </button>
                        </div> : <Button onClick={() => handleAddToCart(product)} disabled={isClosed} size="sm" className={`rounded-full px-4 active:scale-95 transition-transform ${isClosed ? 'bg-gray-200 text-gray-400 hover:bg-gray-200 cursor-not-allowed' : 'bg-[#111827] hover:bg-gray-800 text-white'}`} data-testid={`add-to-cart-${product.id}`}>

                          <Plus className="w-4 h-4 mr-1" />{t("Ajouter")}

                    </Button>}
                    </div>
                  </div>
                </motion.div></div>;
        })}
      </div>

      <Dialog open={!!optionsModal} onOpenChange={open => {
      if (!open) {
        setOptionsModal(null);
        setSelectedOptions([]);
      }
    }}>

        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{
            fontFamily: 'Manrope'
          }}>{optionsModal?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-500">{optionsModal?.description}</p>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">{t("Personnalisez votre commande :")}</p>

              {optionsModal?.options?.map(opt => <label key={opt.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors" data-testid={`option-${opt.name.replace(/\s+/g, '-').toLowerCase()}`}>

                  <div className="flex items-center gap-3">
                    <Checkbox checked={selectedOptions.includes(opt.name)} onCheckedChange={() => toggleOption(opt.name)} />

                    <span className="text-sm font-medium">{opt.name}</span>
                  </div>

                  {opt.price > 0 ? <span className="text-sm font-semibold text-[#7C3AED]" style={{
                fontFamily: 'JetBrains Mono'
              }}>

                      +{formatMoney(opt.price)}
                    </span> : <span className="text-xs text-gray-400">{t("Gratuit")}</span>}
                </label>)}
            </div>

            <div className="border-t pt-3 flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-500">{t("Total :")}</span>
                <span className="font-bold text-lg" style={{
                fontFamily: 'JetBrains Mono'
              }}>

                  {(() => {
                  const pricing = getProductDisplayPricing(optionsModal || {});
                  return formatMoney(pricing.finalPrice + getOptionsTotal());
                })()}
                </span>
              </div>

              <Button onClick={handleConfirmWithOptions} disabled={isClosed} className={`rounded-full px-6 ${isClosed ? 'bg-gray-200 text-gray-400 hover:bg-gray-200 cursor-not-allowed' : 'bg-[#7C3AED] hover:bg-violet-600 text-white'}`} data-testid="confirm-options-btn">

                <Plus className="w-4 h-4 mr-1" />{t("Ajouter")}

              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {itemCount > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-0 left-0 right-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-white via-white to-transparent">

            <Button onClick={() => navigate('/cart')} data-testid="view-cart-btn" className="w-full bg-[#7C3AED] hover:bg-violet-600 text-white rounded-full py-6 text-lg font-semibold shadow-lg flex items-center justify-center gap-3 active:scale-95 transition-transform">

              <ShoppingCart className="w-5 h-5" />{t("Voir le panier (")}
            {itemCount})
            </Button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
